/**
 * SuperTrend Quantum — Indicador de Régimen Multi-SuperTrend Ponderado
 *
 * Portado fiel al indicador desplegado en producción (app.getquantum.app /
 * getquantum.app/chart). Mantiene los colores de marca Rolex aplicados
 * sobre la misma lógica: consenso de 5 SuperTrends + miembro base.
 */

import { LineStyle } from 'lightweight-charts'
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/logger'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { CloudFillSeries } from './CloudFillSeries.js'

// ── Colores de marca Rolex ─────────────────────────────────────────────────
const COLORS = {
  bull: '#35A07A',
  bear: '#B4574B',
  neutral: '#B08D57',
  bullCloud: 'rgba(53, 160, 122, 0.22)',
  bearCloud: 'rgba(180, 87, 75, 0.22)',
  base: '#B08D57',
  memberInactive: '#B9BDB4',
}

// ── Estado oficial del backend (señal abierta en Redis signal_card) ────────
// El chart lo inyecta vía setBackendSignal() tras consultar
// /api/public/indicators/supertrend/signal. Cuando está cargado tiene
// prioridad sobre el cálculo local para dashboard.activeSignal y la caja de
// posición: una única fuente de verdad (hotfix 2026-08-03). Mismo patrón de
// singleton a nivel de módulo que ict_quantum.
let backendSignal = null
let backendSignalLoaded = false

export function setBackendSignal(signal) {
  backendSignal = signal || null
  backendSignalLoaded = true
}

export function clearBackendSignal() {
  backendSignal = null
  backendSignalLoaded = false
}

// Traduce la señal del backend (direction/entry/sl/tp*/candle_time ms) al
// shape de una señal activa del indicador (time en segundos, price = SL
// inicial, como las cluster labels locales).
function backendToActiveSignal(sig) {
  if (!sig || !sig.direction || !Number.isFinite(sig.entry) || !Number.isFinite(sig.sl)) return null
  return {
    time: Math.floor(sig.candle_time / 1000),
    isBull: sig.direction === 'LONG',
    entry: sig.entry,
    price: sig.sl,
    sl: sig.sl,
    tp1: sig.tp1,
    tp2: sig.tp2,
    outcome: 'OPEN',
    active: true,
    backend: true,
  }
}

function resolveActiveSignal(localActiveSignal) {
  return backendSignalLoaded ? backendToActiveSignal(backendSignal) : localActiveSignal
}

// ── Estilo Quantum por defecto (todo configurable, este es el baseline) ───────
const QUANTUM_STYLE = {
  markerSize: 1,
  haloOpacity: 0.25,
  cloudOpacity: 0.18,
  baseLineWidth: 1,
  baseLineStyle: 'dotted',
  memberLineWidth: 1,
  memberLineStyle: 'dotted',
  labelFont: "'Playfair Display', Georgia, serif",
  labelBgOpacity: 0.12,
  plaqueStyle: 'plaque',
}

// ── Config por defecto ─────────────────────────────────────────────────────
export const defaultConfig = {
  // ── Lógica ───────────────────────────────────────────────────────────────
  consensus_threshold: 0.60,
  base_index: 3,
  cloud_length: 8,
  // cloud_transparency antiguo reemplazado por cloudOpacity real en el renderer
  show_cloud: true,
  show_base_line: true,
  show_member_lines: false,
  show_base_flip_dots: true,
  show_signals: true,
  show_cluster_labels: true,
  dynamic_bar_coloring: false,
  showPriceLabel: true,
  showPositionBox: true,

  // ── Dashboard / Panel ──────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},

  // ── Estilo ───────────────────────────────────────────────────────────────
  colorBull: '#35A07A',
  colorBear: '#B4574B',
  colorNeutral: '#B08D57',
  lineWidth: 1.6,
  ...QUANTUM_STYLE,
}

// ── Resuelve colores por config ────────────────────────────────────────────
function resolveColors(config) {
  return {
    bull: config.colorBull || '#35A07A',
    bear: config.colorBear || '#B4574B',
    neutral: config.colorNeutral || '#B08D57',
  }
}

// ── Helpers de estilo ──────────────────────────────────────────────────────
function lineStyleFromString(style) {
  switch (style) {
    case 'dotted': return LineStyle.Dotted
    case 'dashed': return LineStyle.Dashed
    case 'largeDashed': return LineStyle.LargeDashed
    case 'sparseDotted': return LineStyle.SparseDotted
    case 'solid':
    default: return LineStyle.Solid
  }
}

function colorToRgba(color, alpha) {
  const rgb = hexToRgb(color) || { r: 176, g: 141, b: 87 }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

function withAlpha(color, alpha) {
  const a = Math.max(0, Math.min(1, alpha))
  if (typeof color !== 'string') return `rgba(176,141,87,${a})`
  if (color.startsWith('rgba')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${a})`)
  }
  if (color.startsWith('rgb(')) {
    const parts = color.match(/\d+/g)
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${a})`
  }
  if (color.startsWith('#')) {
    const rgb = hexToRgb(color)
    if (rgb) return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`
  }
  return color
}

// ── Helpers de medias móviles ──────────────────────────────────────────────

function calcSMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  for (let i = len - 1; i < arr.length; i++) {
    let s = 0
    for (let j = i - len + 1; j <= i; j++) s += arr[j]
    out[i] = s / len
  }
  return out
}

function calcEMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  const k = 2 / (len + 1)
  let ema = null
  for (let i = 0; i < arr.length; i++) {
    if (ema === null && i >= len - 1) {
      let s = 0
      for (let j = i - len + 1; j <= i; j++) s += arr[j]
      ema = s / len
    }
    if (ema !== null) {
      ema = arr[i] * k + ema * (1 - k)
      out[i] = ema
    }
  }
  return out
}

function calcWMA(arr, len) {
  // Fórmula WMA exacta del bundle de producción (función Ga en el build
  // desplegado en getquantum.app). No es la WMA estándar de muchos libros,
  // pero mantiene paridad numérica con producción: se conserva tal cual.
  const out = new Array(arr.length).fill(null)
  for (let i = len - 1; i < arr.length; i++) {
    let num = 0
    let den = 0
    for (let j = i - len + 1; j <= i; j++) {
      const w = j - (i - len)
      num += arr[j] * w
      den += w
    }
    out[i] = num / den
  }
  return out
}

function calcHMA(arr, len) {
  const halfLen = Math.max(1, Math.floor(len / 2))
  const sqrtLen = Math.max(1, Math.floor(Math.sqrt(len)))
  const halfWma = calcWMA(arr, halfLen)
  const fullWma = calcWMA(arr, len)
  const diff = arr.map((_, i) => 2 * halfWma[i] - fullWma[i])
  return calcWMA(diff, sqrtLen)
}

function calcRMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  const alpha = 1 / len
  let rma = null
  for (let i = 0; i < arr.length; i++) {
    if (rma === null && i >= len - 1) {
      let s = 0
      for (let j = i - len + 1; j <= i; j++) s += arr[j]
      rma = s / len
    }
    if (rma !== null) {
      rma = alpha * arr[i] + (1 - alpha) * rma
      out[i] = rma
    }
  }
  return out
}

function calcLSMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  for (let i = len - 1; i < arr.length; i++) {
    const win = arr.slice(i - len + 1, i + 1)
    const n = win.length
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0
    for (let j = 0; j < n; j++) {
      sumX += j
      sumY += win[j]
      sumXY += j * win[j]
      sumX2 += j * j
    }
    const den = n * sumX2 - sumX * sumX
    if (Math.abs(den) < 1e-9) {
      out[i] = win[n - 1]
      continue
    }
    const slope = (n * sumXY - sumX * sumY) / den
    const intercept = (sumY - slope * sumX) / n
    out[i] = intercept + slope * (n - 1)
  }
  return out
}

function calcMA(arr, type, len) {
  switch (type) {
    case 'SMA': return calcSMA(arr, len)
    case 'EMA': return calcEMA(arr, len)
    case 'WMA': return calcWMA(arr, len)
    case 'HMA': return calcHMA(arr, len)
    case 'RMA': return calcRMA(arr, len)
    case 'LSMA': return calcLSMA(arr, len)
    default: return calcSMA(arr, len)
  }
}

// ── ATR ────────────────────────────────────────────────────────────────────

function calcATR(candles, period) {
  const n = candles.length
  const get = (i, field) => candles[i][field]

  const tr = new Array(n).fill(0)
  for (let i = 0; i < n; i++) {
    const high = get(i, 'high')
    const low = get(i, 'low')
    const close = get(i, 'close')
    const prevClose = i === 0 ? close : get(i - 1, 'close')
    tr[i] = Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose))
  }

  const out = new Array(n).fill(null)
  if (n < period) return out
  let sum = 0
  for (let i = 0; i < period; i++) sum += tr[i]
  out[period - 1] = sum / period
  for (let i = period; i < n; i++) {
    out[i] = (out[i - 1] * (period - 1) + tr[i]) / period
  }
  return out
}

// ── SuperTrend individual ──────────────────────────────────────────────────

function calcSuperTrend(src, high, low, close, atrLen, factor) {
  const n = src.length
  const atr = calcATR(
    Array.from({ length: n }, (_, i) => ({ high: high[i], low: low[i], close: close[i] })),
    atrLen
  )
  const ub0 = new Array(n).fill(null)
  const lb0 = new Array(n).fill(null)
  for (let i = 0; i < n; i++) {
    ub0[i] = src[i] + factor * atr[i]
    lb0[i] = src[i] - factor * atr[i]
  }

  const ub = new Array(n).fill(null)
  const lb = new Array(n).fill(null)
  const d = new Array(n).fill(null)
  const st = new Array(n).fill(null)

  for (let i = 0; i < n; i++) {
    if (i === 0 || atr[i] == null) {
      ub[i] = ub0[i]
      lb[i] = lb0[i]
      d[i] = 1
      st[i] = lb0[i]
      continue
    }
    ub[i] = (ub0[i] < ub[i - 1] || src[i - 1] > ub[i - 1]) ? ub0[i] : ub[i - 1]
    lb[i] = (lb0[i] > lb[i - 1] || src[i - 1] < lb[i - 1]) ? lb0[i] : lb[i - 1]

    if (d[i - 1] === -1 && src[i] > ub[i - 1]) d[i] = 1
    else if (d[i - 1] === 1 && src[i] < lb[i - 1]) d[i] = -1
    else d[i] = d[i - 1]

    st[i] = d[i] === 1 ? lb[i] : ub[i]
  }
  return { st, d }
}

// ── Miembros por defecto ───────────────────────────────────────────────────

const DEFAULT_MEMBERS = [
  { id: 1, name: 'Rápido', atr: 7, factor: 1.5, smoothing: 'EMA', length: 3, weight: 1.0 },
  { id: 2, name: 'Medio-Rápido', atr: 10, factor: 2.0, smoothing: 'EMA', length: 5, weight: 1.0 },
  { id: 3, name: 'Base', atr: 14, factor: 2.5, smoothing: 'SMA', length: 8, weight: 1.2 },
  { id: 4, name: 'Medio-Lento', atr: 21, factor: 3.0, smoothing: 'WMA', length: 13, weight: 1.4 },
  { id: 5, name: 'Lento', atr: 34, factor: 4.0, smoothing: 'HMA', length: 21, weight: 1.6 },
]

// ── detect() ───────────────────────────────────────────────────────────────

export function detect(candles, config) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  const cfg = { ...defaultConfig, ...(config || {}) }
  const n = candles.length
  if (n < 50) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    // eslint-disable-next-line no-console
    debugLog(`[supertrend_quantum] detect empty: ${(t1 - t0).toFixed(2)}ms`)
    return emptyResult()
  }
  const open = candles.map(c => c.open)
  const high = candles.map(c => c.high)
  const low = candles.map(c => c.low)
  const close = candles.map(c => c.close)
  const time = candles.map(c => c.time)
  const hlc3 = close.map((_, i) => (high[i] + low[i] + close[i]) / 3)

  // Paso 1: miembros
  const members = DEFAULT_MEMBERS.map(m => ({
    ...m,
    src: calcMA(hlc3, m.smoothing, m.length),
  }))

  const memberResults = members.map(m => ({
    ...m,
    ...calcSuperTrend(m.src, high, low, close, m.atr, m.factor),
  }))

  // Paso 2: consensus
  const wSum = members.reduce((a, m) => a + m.weight, 0)
  const scBull = new Array(n).fill(0)
  const scBear = new Array(n).fill(0)
  const lnBull = new Array(n).fill(null)
  const lnBear = new Array(n).fill(null)

  for (let i = 0; i < n; i++) {
    let wBu = 0, wBe = 0
    let vBu = 0, vBe = 0
    let wLnBu = 0, wLnBe = 0
    for (const m of memberResults) {
      if (m.d[i] == null || m.st[i] == null) continue
      if (m.d[i] > 0) {
        wBu += m.weight
        wLnBu += m.weight
        vBu += m.st[i] * m.weight
      } else if (m.d[i] < 0) {
        wBe += m.weight
        wLnBe += m.weight
        vBe += m.st[i] * m.weight
      }
    }
    scBull[i] = wSum > 0 ? wBu / wSum : 0
    scBear[i] = wSum > 0 ? wBe / wSum : 0
    lnBull[i] = wLnBu > 0 ? vBu / wLnBu : null
    lnBear[i] = wLnBe > 0 ? vBe / wLnBe : null
  }

  const scCluster = scBull.map((v, i) => v - scBear[i])
  const strength = scCluster.map(v => Math.abs(v))

  // Paso 3: filtro base
  const baseIndex = Math.max(1, Math.min(5, cfg.base_index)) - 1
  const base = memberResults[baseIndex]
  const threshold = cfg.consensus_threshold

  const dLast = new Array(n).fill(0)
  let currentDlast = 0
  for (let i = 0; i < n; i++) {
    const okBull = scBull[i] >= threshold && base.d[i] > 0
    const okBear = scBear[i] >= threshold && base.d[i] < 0
    if (okBull && !okBear) currentDlast = 1
    else if (okBear && !okBull) currentDlast = -1
    dLast[i] = currentDlast
  }

  const lnCluster = new Array(n).fill(null)
  for (let i = 0; i < n; i++) {
    if (dLast[i] > 0) lnCluster[i] = lnBull[i]
    else if (dLast[i] < 0) lnCluster[i] = lnBear[i]
  }

  // Señales
  const signalLong = dLast.map((v, i) => i > 0 && dLast[i - 1] <= 0 && v > 0)
  const signalShort = dLast.map((v, i) => i > 0 && dLast[i - 1] >= 0 && v < 0)
  const baseFlipBull = base.d.map((v, i) => i > 0 && base.d[i - 1] <= 0 && v > 0)
  const baseFlipBear = base.d.map((v, i) => i > 0 && base.d[i - 1] >= 0 && v < 0)

  const clusterLabels = []
  for (let i = 1; i < n; i++) {
    if (dLast[i] === dLast[i - 1] || dLast[i] === 0) continue
    clusterLabels.push({
      time: time[i],
      index: i,
      isBull: dLast[i] > 0,
      pct: strength[i] || 0,
      price: lnCluster[i] ?? close[i],
      entry: close[i],
    })
  }

  // Cloud ref
  const cloudRef = calcSMA(hlc3, cfg.cloud_length)

  // Regimen string
  const regime = dLast.map(v => (v > 0 ? 'BULL' : v < 0 ? 'BEAR' : 'NEUTRAL'))

  // Historial de señales: últimas 50 señales o 90 días, lo que dé más muestra.
  const SIGNAL_WINDOW_DAYS = 90
  const SIGNAL_WINDOW_COUNT = 50
  const cutoffTime = Date.now() / 1000 - SIGNAL_WINDOW_DAYS * 24 * 3600
  const sortedSignals = [...clusterLabels].sort((a, b) => b.time - a.time)
  const windowedSignals = []
  for (const sig of sortedSignals) {
    if (windowedSignals.length >= SIGNAL_WINDOW_COUNT) break
    if (sig.time < cutoffTime && windowedSignals.length > 0) break
    windowedSignals.push(sig)
  }
  const signalHistory = windowedSignals
    .map(sig => ({ ...sig, ...evaluateSignalOutcome(candles, sig.index, sig) }))
    .sort((a, b) => a.time - b.time)

  const totalSignals = signalHistory.length
  const tp1Count = signalHistory.filter(s => s.outcome === 'TP1').length
  const tp2Count = signalHistory.filter(s => s.outcome === 'TP2').length
  const slCount = signalHistory.filter(s => s.outcome === 'SL').length
  const beCount = signalHistory.filter(s => s.outcome === 'BE').length
  const avgR = totalSignals > 0
    ? signalHistory.reduce((sum, s) => {
        if (s.outcome === 'TP2') return sum + 2
        if (s.outcome === 'TP1') return sum + 1
        if (s.outcome === 'BE') return sum + 0
        if (s.outcome === 'SL') return sum - 1
        return sum + 0
      }, 0) / totalSignals
    : 0

  const latestSignal = signalHistory.length > 0 ? signalHistory[signalHistory.length - 1] : null

  // Señal viva: la última señal sin señal contraria posterior y que no haya cerrado (SL/TP2/BE).
  // Si la última señal del historial es OPEN o TP1, es la viva. Si no, buscamos en la serie completa.
  const activeSignal = (() => {
    if (clusterLabels.length === 0) return null
    for (let i = clusterLabels.length - 1; i >= 0; i--) {
      const sig = clusterLabels[i]
      const hasContraryAfter = clusterLabels.some(l => l.time > sig.time && l.isBull !== sig.isBull)
      if (hasContraryAfter) continue
      const outcome = evaluateSignalOutcome(candles, sig.index, sig)
      if (outcome.outcome === 'OPEN' || outcome.outcome === 'TP1') {
        return { ...sig, ...outcome }
      }
      // Si la última señal sin contraria posterior está cerrada, no hay señal viva.
      return null
    }
    return null
  })()

  const history = {
    totalSignals,
    tp1Rate: totalSignals > 0 ? tp1Count / totalSignals : 0,
    tp2Rate: totalSignals > 0 ? tp2Count / totalSignals : 0,
    slRate: totalSignals > 0 ? slCount / totalSignals : 0,
    beRate: totalSignals > 0 ? beCount / totalSignals : 0,
    avgR,
    signals: signalHistory,
  }

  // Coloreado de velas
  const theme = resolveColors(cfg)
  const coloredCandles = cfg.dynamic_bar_coloring
    ? candles.map((c, i) => {
        const st = strength[i] || 0
        const sc = scCluster[i] || 0
        const color = sc > 0
          ? interpolateColor(theme.neutral, theme.bull, st)
          : interpolateColor(theme.neutral, theme.bear, st)
        return { ...c, color, borderColor: color, wickColor: color }
      })
    : null

  // Datos para render
  const clusterData = []
  const baseData = []
  const cloudRefData = []
  const memberLinesData = memberResults.map(() => [])
  const cloudData = []
  const markers = []

  for (let i = 0; i < n; i++) {
    const t = time[i]
    if (Number.isFinite(lnCluster[i])) {
      clusterData.push({
        time: t,
        value: lnCluster[i],
        color: regime[i] === 'BULL' ? theme.bull : theme.bear,
      })
    }
    if (cfg.show_base_line && Number.isFinite(base.st[i])) {
      baseData.push({ time: t, value: base.st[i] })
    }
    if (Number.isFinite(cloudRef[i])) {
      cloudRefData.push({ time: t, value: cloudRef[i] })
    }

    if (cfg.show_member_lines) {
      memberResults.forEach((m, idx) => {
        if (Number.isFinite(m.st[i])) {
          memberLinesData[idx].push({ time: t, value: m.st[i] })
        }
      })
    }

    if (cfg.show_cloud && Number.isFinite(lnCluster[i]) && Number.isFinite(cloudRef[i])) {
      const upper = Math.max(lnCluster[i], cloudRef[i])
      const lower = Math.min(lnCluster[i], cloudRef[i])
      const segColor = dLast[i] > 0 ? theme.bull : dLast[i] < 0 ? theme.bear : theme.neutral
      cloudData.push({ time: t, upper, lower, color: segColor })
    }

    if (cfg.show_signals !== false && signalLong[i]) {
      markers.push({
        time: t,
        position: 'belowBar',
        color: theme.bull,
        shape: 'arrowUp',
        size: cfg.markerSize ?? 1,
      })
      // Halo suave
      markers.push({
        time: t,
        position: 'belowBar',
        color: withAlpha(theme.bull, cfg.haloOpacity ?? 0.25),
        shape: 'circle',
        size: (cfg.markerSize ?? 1) + 1,
      })
    }
    if (cfg.show_signals !== false && signalShort[i]) {
      markers.push({
        time: t,
        position: 'aboveBar',
        color: theme.bear,
        shape: 'arrowDown',
        size: cfg.markerSize ?? 1,
      })
      markers.push({
        time: t,
        position: 'aboveBar',
        color: withAlpha(theme.bear, cfg.haloOpacity ?? 0.25),
        shape: 'circle',
        size: (cfg.markerSize ?? 1) + 1,
      })
    }
    if (cfg.show_base_flip_dots && baseFlipBull[i]) {
      markers.push({ time: t, position: 'inBar', color: theme.bull, shape: 'circle', size: cfg.markerSize ?? 1 })
    }
    if (cfg.show_base_flip_dots && baseFlipBear[i]) {
      markers.push({ time: t, position: 'inBar', color: theme.bear, shape: 'circle', size: cfg.markerSize ?? 1 })
    }
  }

  const lastIdx = n - 1
  const dashboard = {
    regime: regime[lastIdx],
    strength: strength[lastIdx],
    bullScore: scBull[lastIdx],
    bearScore: scBear[lastIdx],
    baseDir: base.d[lastIdx] > 0 ? 'BULL' : base.d[lastIdx] < 0 ? 'BEAR' : 'NEUTRAL',
    members: memberResults.map(m => ({
      id: m.id,
      name: m.name,
      dir: m.d[lastIdx] > 0 ? 'BULL' : m.d[lastIdx] < 0 ? 'BEAR' : 'NEUTRAL',
    })),
    history,
    latestSignal,
    activeSignal: resolveActiveSignal(activeSignal),
  }

    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    // eslint-disable-next-line no-console
    debugLog(`[supertrend_quantum] detect done: ${(t1 - t0).toFixed(2)}ms (n=${n})`)

    return {
    clusterData,
    baseData,
    cloudRefData,
    cloudData,
    memberLinesData,
    markers,
    clusterLabels,
    coloredCandles,
    dashboard,
    scores: {
      bull: scBull,
      bear: scBear,
      strength,
      dlast: dLast,
      regime,
    },
  }
}

function emptyResult() {
  return {
    clusterData: [],
    baseData: [],
    cloudRefData: [],
    cloudData: [],
    memberLinesData: [],
    markers: [],
    clusterLabels: [],
    coloredCandles: null,
    dashboard: {
      regime: 'NEUTRAL',
      strength: 0,
      bullScore: 0,
      bearScore: 0,
      baseDir: 'NEUTRAL',
      members: [],
      history: {
        totalSignals: 0,
        tp1Rate: 0,
        tp2Rate: 0,
        slRate: 0,
        beRate: 0,
        avgR: 0,
        signals: [],
      },
      latestSignal: null,
      activeSignal: resolveActiveSignal(null),
    },
    scores: { bull: [], bear: [], strength: [], dlast: [], regime: [] },
  }
}

// Evalúa el resultado de una señal de cluster siguiendo la escalera BE:
// - El setup se activa si la entrada es tocada por alguna vela posterior.
// - Se evalúa por toque de mecha (high/low), no por cierre.
// - Al recorrer 2/3 de la distancia entrada→TP1 se activa el BE: el SL sube a
//   la entrada. Si el precio no alcanza ese 2/3 y toca el SL original → SL.
// - TP1 tocado → la línea BE sube a TP1; si el precio se gira a esa línea, el
//   trade cuenta como TP1 (+1R).
// - TP2 tocado → trade cerrado como TP2 (+2R).
// - BE activo sin TP1 y vuelta a la entrada → BE (0R).
// - Conquistas persistentes; intra-barra ambigua → conservador (gana el SL).
export function evaluateSignalOutcome(candles, signalIdx, signal) {
  const isLong = signal.isBull
  const entry = signal.entry
  const initialSl = signal.price
  const r = Math.abs(entry - initialSl)

  if (signalIdx < 0 || signalIdx >= candles.length || r <= 0 || !Number.isFinite(entry) || !Number.isFinite(initialSl)) {
    return { active: false, tp1Hit: false, tp2Hit: false, slHit: false, beHit: false, finalSl: null, r: 0, outcome: 'NONE' }
  }

  const dir = isLong ? 1 : -1
  const tp1 = entry + dir * r
  const tp2 = entry + dir * 2 * r
  const beTrigger = entry + dir * (2 / 3) * r

  // Setup activado: la entrada debe haber sido tocada por alguna vela posterior.
  let active = false
  for (let i = signalIdx + 1; i < candles.length; i++) {
    const c = candles[i]
    if (c.low <= entry && c.high >= entry) {
      active = true
      break
    }
  }

  if (!active) {
    return { active: false, tp1Hit: false, tp2Hit: false, slHit: false, beHit: false, finalSl: initialSl, r, outcome: 'NOT_ACTIVATED' }
  }

  let tp1Hit = false
  let tp2Hit = false
  let slHit = false
  let beHit = false
  let sl = initialSl
  let outcome = 'OPEN'

  const touchesPrice = (price, candle) => price >= candle.low && price <= candle.high

  for (let i = signalIdx + 1; i < candles.length; i++) {
    const c = candles[i]

    // TP2: trade cerrado en +2R. Con TP1 ya tocado, el toque de TP2 manda: la
    // vuelta a la línea BE (antigua TP1) solo se evalúa en velas posteriores.
    if (!tp2Hit && touchesPrice(tp2, c)) {
      if (!tp1Hit && touchesPrice(sl, c)) {
        // Intra-barra ambigua TP2/SL: conservador, gana el SL actual.
        slHit = true
        outcome = beHit ? 'BE' : 'SL'
        break
      }
      tp2Hit = true
      tp1Hit = true
      beHit = true
      sl = tp2
      outcome = 'TP2'
      break
    }

    // TP1: la línea BE sube a TP1; el trade ya cuenta como TP1 salvo que siga a TP2.
    if (!tp1Hit && touchesPrice(tp1, c)) {
      if (touchesPrice(sl, c)) {
        // Intra-barra ambigua TP1/SL: conservador, gana el SL actual.
        slHit = true
        outcome = beHit ? 'BE' : 'SL'
        break
      }
      tp1Hit = true
      beHit = true
      sl = tp1
      outcome = 'TP1'
      continue
    }

    // Activación del BE al recorrer 2/3 de la distancia entrada→TP1.
    if (!beHit && touchesPrice(beTrigger, c)) {
      if (touchesPrice(sl, c)) {
        // Intra-barra ambigua BE/SL: conservador, gana el SL original.
        slHit = true
        outcome = 'SL'
        break
      }
      beHit = true
      sl = entry
      // El BE recién subido a la entrada no se evalúa en esta misma vela: no
      // se puede ordenar el toque del 2/3 y el de la entrada dentro de ella.
      continue
    }

    // SL actual: original → SL; en entrada (BE activo) → BE; en TP1 → TP1.
    if (touchesPrice(sl, c)) {
      slHit = true
      outcome = tp1Hit ? 'TP1' : beHit ? 'BE' : 'SL'
      break
    }
  }

  return { active, tp1Hit, tp2Hit, slHit, beHit, finalSl: sl, r, outcome }
}

// ── render() ───────────────────────────────────────────────────────────────

function sanitizeColoredCandles(arr) {
  const sorted = [...arr]
    .filter(c => c && c.time != null && c.time !== '' && !Number.isNaN(c.time)
      && [c.open, c.high, c.low, c.close].every(Number.isFinite))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const c of sorted) {
    if (out.length > 0 && out[out.length - 1].time === c.time) out[out.length - 1] = c
    else out.push(c)
  }
  return out
}

export function deduplicateMarkers(markers) {
  if (!Array.isArray(markers)) return []
  const valid = markers.filter(m => m && m.time != null && m.time !== '' && !Number.isNaN(m.time))
  const grouped = {}
  for (const m of valid) {
    if (!grouped[m.time]) grouped[m.time] = []
    grouped[m.time].push(m)
  }
  const priority = (m) => {
    if (m.shape === 'arrowUp' || m.shape === 'arrowDown') return 3
    if (m.position === 'belowBar' || m.position === 'aboveBar') return 2
    return 1
  }
  const out = []
  for (const t of Object.keys(grouped).sort((a, b) => Number(a) - Number(b))) {
    const group = grouped[t]
    if (group.length === 1) {
      out.push(group[0])
    } else {
      group.sort((a, b) => priority(b) - priority(a))
      out.push(group[0])
    }
  }
  return out
}

function cleanValues(arr) {
  if (!Array.isArray(arr)) return []
  const sorted = arr
    .filter(d => d && d.time != null && d.time !== '' && !Number.isNaN(d.time) && Number.isFinite(d.value))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const d of sorted) {
    if (out.length > 0 && out[out.length - 1].time === d.time) out[out.length - 1] = d
    else out.push(d)
  }
  return out
}

function cleanCloudData(arr) {
  if (!Array.isArray(arr)) return []
  const sorted = arr
    .filter(d => d && d.time != null && d.time !== '' && !Number.isNaN(d.time) && Number.isFinite(d.upper) && Number.isFinite(d.lower))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const d of sorted) {
    if (out.length > 0 && out[out.length - 1].time === d.time) out[out.length - 1] = d
    else out.push(d)
  }
  return out
}

export function render(chart, candleSeries, result, config, api) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!result || !result.clusterData) return
  // Guarda de velas: registrar longitud antes de renderizar; si cambia, es bug.
  if (candleSeries && typeof candleSeries.data === 'function') {
    candleSeries.__stqCandleCountBeforeRender = candleSeries.data().length
  }
  const cfg = { ...defaultConfig, ...(config || {}) }
  const theme = resolveColors(cfg)
  const lineWidth = cfg.lineWidth ?? 1.6

  // Línea de cluster: separada en bull/bear para que el color cambie con el régimen
  const clusterBull = cleanValues(result.clusterData.filter(d => d.color === theme.bull))
  const clusterBear = cleanValues(result.clusterData.filter(d => d.color === theme.bear))

  const showPriceLabel = cfg.showPriceLabel ?? true

  const clusterBullSeries = api.addSeries(c => c.addLineSeries({
    title: 'STQ Cluster+',
    color: theme.bull,
    lineWidth,
    lineStyle: LineStyle.Solid,
    lastValueVisible: showPriceLabel,
    priceLineVisible: false,
  }))
  if (clusterBullSeries) clusterBullSeries.setData(clusterBull)

  const clusterBearSeries = api.addSeries(c => c.addLineSeries({
    title: 'STQ Cluster-',
    color: theme.bear,
    lineWidth,
    lineStyle: LineStyle.Solid,
    lastValueVisible: showPriceLabel,
    priceLineVisible: false,
  }))
  if (clusterBearSeries) clusterBearSeries.setData(clusterBear)

  // Placas in-chart al final de cada serie (evitan solaparse en el eje de precios)
  const lastClusterBull = clusterBull[clusterBull.length - 1]
  const lastClusterBear = clusterBear[clusterBear.length - 1]
  if (lastClusterBull) {
    api.addLabel({
      barsFromLast: 1,
      price: lastClusterBull.value,
      text: 'STQ Cluster+',
      color: theme.bull,
      bgColor: withAlpha(theme.bull, cfg.labelBgOpacity ?? 0.12),
      boxStyle: 'plaque',
      isBull: true,
      font: cfg.labelFont,
    })
  }
  if (lastClusterBear) {
    api.addLabel({
      barsFromLast: 1,
      price: lastClusterBear.value,
      text: 'STQ Cluster-',
      color: theme.bear,
      bgColor: withAlpha(theme.bear, cfg.labelBgOpacity ?? 0.12),
      boxStyle: 'plaque',
      isBull: false,
      font: cfg.labelFont,
    })
  }

  // Línea base (miembro base): dorada, fina, estilo configurable
  const tBaseStart = typeof performance !== 'undefined' ? performance.now() : 0
  const cleanBase = cleanValues(result.baseData)
  if (cfg.show_base_line) {
    const baseSeries = api.addSeries(c => c.addLineSeries({
      title: 'STQ Base',
      color: theme.neutral,
      lineWidth: cfg.baseLineWidth ?? 1,
      lineStyle: lineStyleFromString(cfg.baseLineStyle),
      lastValueVisible: false,
      priceLineVisible: false,
    }))
    if (baseSeries) {
      baseSeries.setData(cleanBase)
      const lastBase = cleanBase[cleanBase.length - 1]
      if (lastBase) {
        api.addLabel({
          barsFromLast: 1,
          price: lastBase.value,
          text: 'STQ Base',
          color: theme.neutral,
          bgColor: withAlpha(theme.neutral, cfg.labelBgOpacity ?? 0.12),
          boxStyle: 'plaque',
          isBull: true,
          font: cfg.labelFont,
        })
      }
    }
  }
  const tBaseEnd = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] base line: ${(tBaseEnd - tBaseStart).toFixed(2)}ms`)

  // Líneas individuales de miembros (debug)
  const tMembersStart = typeof performance !== 'undefined' ? performance.now() : 0
  if (cfg.show_member_lines) {
    result.memberLinesData.forEach((data, idx) => {
      const s = api.addSeries(c => c.addLineSeries({
        title: `ST${idx + 1}`,
        color: theme.neutral,
        lineWidth: cfg.memberLineWidth ?? 1,
        lineStyle: lineStyleFromString(cfg.memberLineStyle),
        lastValueVisible: false,
        visible: true,
      }))
      if (s) s.setData(cleanValues(data))
    })
  }
  const tMembersEnd = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] member lines: ${(tMembersEnd - tMembersStart).toFixed(2)}ms`)

  // Ribbon/nube con gradiente vertical suave — opacidad configurable
  const tCloudStart = typeof performance !== 'undefined' ? performance.now() : 0
  if (cfg.show_cloud && result.cloudData.length > 0) {
    const cloudSeries = api.addCustomSeries(new CloudFillSeries(), {
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(0,0,0,0)',
      lineWidth: 0,
      lastValueVisible: false,
      cloudOpacity: cfg.cloudOpacity ?? 0.18,
    })
    if (cloudSeries) cloudSeries.setData(cleanCloudData(result.cloudData))
  }
  const tCloudEnd = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] cloud: ${(tCloudEnd - tCloudStart).toFixed(2)}ms`)

  // Markers: triángulos discretos + halos (generados en detect)
  const tMarkersStart = typeof performance !== 'undefined' ? performance.now() : 0
  const cleanMarkers = deduplicateMarkers(result.markers || [])
  cleanMarkers.forEach(m => api.addMarker(m))
  const tMarkersEnd = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] markers: ${(tMarkersEnd - tMarkersStart).toFixed(2)}ms`)

  // Etiquetas "Bull/Bear Cluster XX%" en placa estilo Rolex
  const tLabelsStart = typeof performance !== 'undefined' ? performance.now() : 0
  if (cfg.show_cluster_labels) {
    result.clusterLabels.forEach(l => {
      const color = l.isBull ? theme.bull : theme.bear
      api.addLabel({
        time: l.time,
        price: l.price,
        isBull: l.isBull,
        color,
        bgColor: withAlpha(color, cfg.labelBgOpacity ?? 0.12),
        boxStyle: cfg.plaqueStyle || 'plaque',
        text: `${l.isBull ? 'Bull' : 'Bear'} Cluster ${(l.pct * 100).toFixed(1)}%`,
        font: cfg.labelFont,
      })
    })
  }
  const tLabelsEnd = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] cluster labels: ${(tLabelsEnd - tLabelsStart).toFixed(2)}ms`)

  // Coloreado de velas (opcional, apagado por defecto)
  const tCandleColorStart = typeof performance !== 'undefined' ? performance.now() : 0
  if (cfg.dynamic_bar_coloring && result.coloredCandles) {
    const sanitized = sanitizeColoredCandles(result.coloredCandles)
    if (sanitized.length > 0 && api.setCandleColors) {
      const colorMap = new Map()
      for (const c of sanitized) {
        colorMap.set(c.time, { color: c.color, borderColor: c.borderColor, wickColor: c.wickColor })
      }
      api.setCandleColors(colorMap)
    } else if (api.setCandleColors) {
      // eslint-disable-next-line no-console
      debugLog('[supertrend_quantum] coloredCandles empty or invalid; leaving candle colors unchanged')
      api.setCandleColors(null)
    }
  } else if (api.setCandleColors) {
    api.setCandleColors(null)
  }
  const tCandleColorEnd = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] candle colors: ${(tCandleColorEnd - tCandleColorStart).toFixed(2)}ms`)

  // Caja de posición LONG/SHORT al estilo ICT/SMC — viva mientras SL no tocado
  // y no haya aparecido una señal contraria posterior.
  const tPositionStart = typeof performance !== 'undefined' ? performance.now() : 0
  if (cfg.showPositionBox !== false) {
    try {
      const allCandles = candleSeries?.data ? candleSeries.data() : []
      // La señal viva se precalcula en detect() (dashboard.activeSignal) para que
      // dashboard y caja de posición hablen el mismo idioma.
      const activeSignal = result.dashboard?.activeSignal
      if (allCandles.length > 0 && activeSignal) {
        const signalIdx = allCandles.findIndex(c => String(c.time) === String(activeSignal.time))
        if (signalIdx !== -1) {
          // Señal oficial del backend: el estado (viva/cerrada) y los niveles
          // los lleva el backend (tracking SL/TP propio); no se re-evalúa el
          // outcome local con la escalera BE.
          const outcome = activeSignal.backend
            ? { active: true, outcome: 'OPEN', r: Math.abs(activeSignal.entry - activeSignal.sl), finalSl: activeSignal.sl, tp1Hit: false, tp2Hit: false, slHit: false, beHit: false }
            : evaluateSignalOutcome(allCandles, signalIdx, activeSignal)
          // La caja solo se muestra mientras la señal esté viva (abierta o TP1 parcial).
          if (outcome.active && (outcome.outcome === 'OPEN' || outcome.outcome === 'TP1') && outcome.r > 0) {
            const isLong = activeSignal.isBull
            const entry = activeSignal.entry
            const r = outcome.r
            const dir = isLong ? 1 : -1
            const tp1 = activeSignal.backend && Number.isFinite(activeSignal.tp1) ? activeSignal.tp1 : entry + dir * r
            const tp2 = activeSignal.backend && Number.isFinite(activeSignal.tp2) ? activeSignal.tp2 : entry + dir * 2 * r
            const sl = outcome.finalSl

            const priceLineColor = isLong ? theme.bull : theme.bear
            const slColor = outcome.beHit ? theme.neutral : '#B4574B'
            const slTitle = outcome.beHit ? 'BE' : 'SL'
            const priceLines = [
              { price: entry, color: priceLineColor, lineWidth: 1, lineStyle: LineStyle.Solid, title: `BE / ${isLong ? 'LONG' : 'SHORT'}` },
              { price: sl, color: slColor, lineWidth: 2, lineStyle: LineStyle.Solid, title: slTitle },
              { price: tp1, color: theme.neutral, lineWidth: 2, lineStyle: LineStyle.Dashed, title: 'TP1 1.0R' },
              { price: tp2, color: theme.neutral, lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'TP2 2.0R' },
            ]
            for (const pl of priceLines) {
              if (Number.isFinite(pl.price)) api.addPriceLine(pl)
            }

            const badges = []
            if (outcome.tp1Hit) badges.push('TP1✓')
            if (outcome.tp2Hit) badges.push('TP2✓')
            if (outcome.slHit) badges.push('SL✗')
            if (outcome.beHit) badges.push('BE')
            const badgeText = badges.length > 0 ? ` · ${badges.join(' ')}` : ''
            const slText = outcome.beHit ? `BE ${sl.toFixed(2)}` : `SL ${activeSignal.price.toFixed(2)}`
            const labelText = `${isLong ? 'LONG' : 'SHORT'} · ${slText}${badgeText} · TP1 ${tp1.toFixed(2)} · TP2 ${tp2.toFixed(2)}`
            api.addLabel({
              barsFromLast: 2,
              price: entry,
              text: labelText,
              color: priceLineColor,
              bgColor: withAlpha(priceLineColor, cfg.labelBgOpacity ?? 0.12),
              boxStyle: 'flag',
              isBull: isLong,
              font: cfg.labelFont,
            })
          }
        }
      }
    } catch (e) {
      debugError('[supertrend_quantum] position box error:', e)
    }
  }
  const tPositionEnd = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] position box: ${(tPositionEnd - tPositionStart).toFixed(2)}ms`)

  // Guarda: este render nunca debe mutar la serie de velas.
  if (candleSeries && typeof candleSeries.data === 'function') {
    const candleCountAfter = candleSeries.data().length
    if (candleCountAfter !== (candleSeries.__stqCandleCountBeforeRender ?? candleCountAfter)) {
      // eslint-disable-next-line no-console
      debugWarn('[supertrend_quantum] candle series length changed during render:', candleSeries.__stqCandleCountBeforeRender, '->', candleCountAfter)
    }
  }

  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[supertrend_quantum] render done: ${(t1 - t0).toFixed(2)}ms`)
}

// ── Interpolación de color ─────────────────────────────────────────────────

function interpolateColor(fromHex, toHex, factor) {
  const f = Math.max(0, Math.min(1, factor))
  const from = hexToRgb(fromHex)
  const to = hexToRgb(toHex)
  if (!from || !to) return fromHex
  const r = Math.round(from.r + (to.r - from.r) * f)
  const g = Math.round(from.g + (to.g - from.g) * f)
  const b = Math.round(from.b + (to.b - from.b) * f)
  return `rgb(${r}, ${g}, ${b})`
}

function hexToRgb(hex) {
  const clean = hex.replace('#', '')
  if (clean.length !== 6) return null
  const bigint = parseInt(clean, 16)
  if (Number.isNaN(bigint)) return null
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 }
}

// ── Registry export ────────────────────────────────────────────────────────

export default {
  id: 'supertrend_quantum',
  name: 'SuperTrend Quantum',
  version: '1.1.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
// TEST_BUILD_1785438935
// TEST_BUILD_1785438966
